<template>
    <div class="company-information-form">
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.country_label')"
            :required="true"
            orientation="horizontal"
        >
            <CompassSelect v-model="selectedCountry" :filterable="true">
                <CompassDropdownSelectOption
                    v-for="item in countries"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                    :flag="item.code.toLowerCase()"
                    type="flag"
                />
            </CompassSelect>
        </CompassFormItem>
        <SirenSearchBar v-if="isFrench" @select="handleResultSelected" />
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.siren_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench && errors.siren"
        >
            <CompassInput
                v-model="siren"
                :error="isFrench && !!errors.siren"
                @input="errors.siren = ''"
                @blur="checkRequiredField('siren')"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.siret_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench && errors.siret"
        >
            <CompassInput
                v-model="siret"
                :error="isFrench && !!errors.siret"
                @input="errors.siret = ''"
                @blur="checkRequiredField('siret')"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.company_name_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench && errors.legalName"
        >
            <CompassInput
                v-model="legalName"
                :error="isFrench && !!errors.legalName"
                @input="errors.legalName = ''"
                @blur="checkRequiredField('legalName')"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.vat_number_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench && errors.vatNumber"
        >
            <CompassInput
                v-model="vatNumber"
                :disabled="isVatExempt"
                :error="isFrench && !!errors.vatNumber"
                @input="errors.vatNumber = ''"
                @blur="!isVatExempt && checkRequiredField('vatNumber')"
            />
            <CompassCheckbox
                :modelValue="false"
                :checked="isVatExempt"
                :label="$t.__('CompanyInformation.legal_information.vat_exempt_label')"
                class="company-information-form__vat-exempt-checkbox"
                @change="handleTaxExemptionChange"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.legal_status_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="legalStatus" />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.share_capital_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="shareCapital">
                <template v-if="showCurrencyLeft" #prefix>
                    {{ numformat.currencysymbol }}
                </template>
                <template v-if="showCurrencyRight" #suffix>
                    {{ numformat.currencysymbol }}
                </template>
            </CompassInput>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.naf_code_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="naf" />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.rcs_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="rcs" />
        </CompassFormItem>
        <CompassButton
            class="company-information-form__save-button"
            :label="$t.__('CompanyInformation.legal_information.save_button')"
            :disabled="disableSubmit"
            :loading="isLoading"
            data-bot="settings-company-info_validate-legal-infos"
            @click="handleSubmit"
        />
    </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import SirenSearchBar from 'vcomponents/common/SirenSearchBar/SirenSearchBar.vue';
import { updateAccountInformation, lockAccountInformation } from 'api/account/account';
const { mapState: corpMapState, mapActions: corpMapActions } = createNamespacedHelpers('corp');
const { mapActions: mapPopupPanelActions } = createNamespacedHelpers('popupPanel');

export default {
    components: {
        SirenSearchBar,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        countries: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            selectedCountry: 'FR',
            siren: this.data.legal_france.siren || '',
            siret: this.data.legal_france.siret || '',
            legalName: this.data.legal_name || '',
            vatNumber: this.data.legal_france.vat || '',
            isVatExempt: false,
            legalStatus: this.data.legal_france.company_type || '',
            shareCapital: this.data.capital || '',
            naf: this.data.legal_france.ape_naf_code || '',
            rcs: this.data.legal_france.rcs_immatriculation || '',
            errors: {
                siren: '',
                siret: '',
                legalName: '',
                vatNumber: '',
            },
            isLoading: false,
        };
    },
    computed: {
        ...corpMapState(['numformat']),
        showCurrencyLeft() {
            return this.numformat.currencypos === 'left' || this.numformat.currencypos === 'both';
        },
        showCurrencyRight() {
            return this.numformat.currencypos === 'right' || this.numformat.currencypos === 'both';
        },
        isFrench() {
            return ['FR', 'GP', 'MQ', 'RE'].includes(this.selectedCountry);
        },
        disableSubmit() {
            if (this.isFrench) {
                if (!this.isVatExempt && !this.vatNumber) {
                    return true;
                }
                return Object.values(this.errors).some(Boolean);
            }
            return false;
        },
    },
    watch: {
        isFrench(newVal) {
            if (!newVal) {
                this.errors = {
                    siren: '',
                    siret: '',
                    legalName: '',
                    vatNumber: '',
                };
            }
        },
    },
    created() {
        EventBus.on('company-information-lock', this.handleSave);
    },
    destroyed() {
        EventBus.off('company-information-lock', this.handleSave);
    },
    methods: {
        ...corpMapActions(['setCompanyInformationLocked']),
        ...mapPopupPanelActions(['open']),
        handleTaxExemptionChange(value) {
            this.isVatExempt = value;
            if (value) {
                this.vatNumber = '';
                this.errors.vatNumber = '';
            }
        },
        handleResultSelected(result) {
            this.siren = result.id;
            this.siret = result.siret;
            this.legalName = result.name;
            this.naf = result.job_code;
            this.errors = {
                ...this.errors,
                siren: '',
                siret: '',
            };

            if (window.pendo?.accountId) {
                window.pendo.track('companyLegalInfoDirectory > used');
            }
        },
        checkRequiredField(field) {
            if (this.isFrench && !this[field]) {
                this.errors[field] = this.$t.__('common.labels.this_field_is_required');
            } else {
                this.errors[field] = '';
            }
        },
        handleSubmit() {
            this.open({
                key: 'companyInformationLockDialog',
                props: {},
            });
        },
        handleSave() {
            this.isLoading = true;

            const payload = {
                country_code: this.selectedCountry,
                legal_name: this.legalName,
                legal_france: {
                    siren: this.siren,
                    siret: this.siret,
                    vat: this.vatNumber,
                    company_type: this.legalStatus,
                    ape_naf_code: this.naf,
                    rcs_immatriculation: this.rcs,
                },
                capital: this.shareCapital,
            };

            updateAccountInformation(payload)
                .then((response) => {
                    this.$emit('update', response.data);
                    lockAccountInformation()
                        .then(() => {
                            this.setCompanyInformationLocked(true);
                            this.open({
                                key: 'companyInformationSuccessDialog',
                                props: {},
                            });
                            this.isLoading = false;
                        })
                        .catch(() => {
                            this.$pushToast({
                                type: 'error',
                                text: this.$t.__('CompanyInformation.legal_information.lock.error'),
                            });
                        });
                })
                .catch((error) => {
                    const apiError = error.response?.data?.error.details;

                    const keys = apiError ? Object.keys(apiError) : null;

                    for (const key of keys) {
                        const shortendKey = key.split('.').pop();

                        if (shortendKey === 'siren') {
                            this.errors.siren = apiError[key];
                        } else if (shortendKey === 'siret') {
                            this.errors.siret = apiError[key];
                        } else if (shortendKey === 'vat') {
                            this.errors.vatNumber = apiError[key];
                        }
                    }

                    this.$pushToast({
                        type: 'error',
                        text: this.$t.__('CompanyInformation.legal_information.save.error'),
                    });
                    this.isLoading = false;
                });
        },
    },
};
</script>
<style lang="scss" scoped>
.company-information-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    &__vat-exempt-checkbox {
        margin-top: var(--spacing-0);
    }

    &__save-button {
        display: block;
        margin-left: auto;
    }
}
</style>
